import { Team } from '../types';
import type { PracticeTheme } from './types';

import { createYoutubeSearchUrl } from '../utils/videoUtils';
import {
  AiServiceError,
  AiServiceErrorType,
  DetailedDrill,
  GeneratedPracticePlan,
  PracticePlanRequest
} from './aiService.types';
import { BASE_PRACTICE_OUTLINE_PROMPT, DRILL_DETAILS_PROMPT, getSportSpecificPrompt } from './prompts';
import { getModelConfig } from './aiModels';
import { callOpenAI, OpenAIMessage } from '../api/ai';

// Get GPT model from environment variable with fallback
const GPT_MODEL = import.meta.env.VITE_GPT_MODEL || 'gpt-4-turbo-preview';

// Get configuration for current model
const currentModelConfig = getModelConfig(GPT_MODEL);



// Create template drills for each position group
const templateDrills: Record<string, any[]> = {
 
  "TEAM_COMPETITION": [
    {
      name: "Ultimate Football",
      setup: "Divide team into two equal squads. Set up a field with end zones at each end. Use a football.",
      execution: "Teams advance the ball by passing. Players cannot run with the ball. Defense tries to intercept or knock down passes. Score by catching a pass in the end zone.",
      coachingPoints: [
        "Encourage teamwork and communication",
        "Keep energy high",
        "Focus on fun while maintaining intensity",
        "Celebrate good plays from both teams"
      ]
    },
    {
      name: "Tug of War Tournament",
      setup: "Set up a tug of war rope with a center marker. Divide team into position groups or mixed teams.",
      execution: "Teams compete in a tournament-style competition. Winners advance to the next round until one team is crowned champion.",
      coachingPoints: [
        "Emphasize proper technique and body positioning",
        "Coordinate pulling efforts with teammates",
        "Build team camaraderie through friendly competition",
        "Celebrate effort regardless of outcome"
      ]
    },
    {
      name: "Powerball Challenge",
      setup: "Set up two goals/end zones. Use a medicine ball or heavy ball. Divide into two teams.",
      execution: "Teams try to advance the ball to score in the opposing team's goal. Can pass, carry, or roll the ball. Defense tries to gain possession.",
      coachingPoints: [
        "Use proper form when handling the heavy ball",
        "Communicate with teammates",
        "Develop strategies for advancing the ball",
        "Maintain high energy throughout the game"
      ]
    },
    {
      name: "Capture the Flag",
      setup: "Divide field in half with a centerline. Each team has a 'flag' in their territory. Divide into two teams.",
      execution: "Teams try to capture the opponent's flag and return it to their side without being tagged. If tagged in opponent's territory, player goes to 'jail' until freed by a teammate.",
      coachingPoints: [
        "Develop offensive and defensive strategies",
        "Use speed, agility, and evasive techniques",
        "Communicate and coordinate with teammates",
        "Think strategically about when to attack vs. defend"
      ]
    },
    {
      name: "Team Relay Challenge",
      setup: "Set up a relay course with football-specific movements (shuttle run, bear crawl, high knees, backpedal, etc.). Divide into position groups.",
      execution: "Teams compete in relay race format. Each player completes the course before tagging the next teammate.",
      coachingPoints: [
        "Focus on proper technique while maintaining speed",
        "Encourage and support teammates",
        "Maintain focus and intensity throughout the relay",
        "Celebrate team accomplishments"
      ]
    }
  ]
};



/**
 * Helper function to ensure there's a Team Competition period in the plan
 */
function ensureTeamCompetitionPeriod(plan: any): void {
  // Check if there's already a competition drill
  const competitionDrills = plan.drills.filter((drill: any) => 
    (drill.name && drill.name.toLowerCase().includes('competition')) ||
    (drill.focus_area && drill.focus_area.toString().toLowerCase().includes('competition')) ||
    (drill.description && drill.description.toLowerCase().includes('competition'))
  );
  
  if (competitionDrills.length === 0) {
    // Get a random team competition game from templates
    const teamCompTemplates = templateDrills["TEAM_COMPETITION"];
    const randomTemplate = teamCompTemplates[Math.floor(Math.random() * teamCompTemplates.length)];
    
    // Create new team competition drill
    const teamCompDrill = {
      name: "Team Competition",
      duration: 10,
      description: `Fun, high-energy team competition: ${randomTemplate.name}`,
      focus_area: "Team",
      position_groups: ["ALL"],
      steps: [
        {
          name: "Team Competition Setup",
          setup: randomTemplate.setup,
          execution: randomTemplate.execution,
          coachingPoints: randomTemplate.coachingPoints
        }
      ]
    };
    
    // Add it to the end of the drills (or before a cooldown if present)
    const cooldownIndex = plan.drills.findIndex((drill: any) => 
      drill.name && drill.name.toLowerCase().includes('cool') &&
      drill.name.toLowerCase().includes('down')
    );
    
    if (cooldownIndex !== -1) {
      plan.drills.splice(cooldownIndex, 0, teamCompDrill);
    } else {
      plan.drills.push(teamCompDrill);
    }
  } else {
    // Check if the existing competition is appropriate (not a scrimmage/7-on-7)
    const inappropriate = competitionDrills.some((drill: any) => {
      const name = (drill.name || '').toLowerCase();
      const desc = (drill.description || '').toLowerCase();
      
      return (name.includes('7-on-7') || name.includes('scrimmage') || 
              desc.includes('7-on-7') || desc.includes('scrimmage'));
    });
    
    if (inappropriate) {
      // Get index of the first inappropriate competition
      const badDrillIndex = plan.drills.findIndex((drill: any) => {
        const name = (drill.name || '').toLowerCase();
        const desc = (drill.description || '').toLowerCase();
        
        return (name.includes('competition') || desc.includes('competition')) &&
               (name.includes('7-on-7') || name.includes('scrimmage') || 
                desc.includes('7-on-7') || desc.includes('scrimmage'));
      });
      
      if (badDrillIndex !== -1) {
        // Get a random team competition game from templates
        const teamCompTemplates = templateDrills["TEAM_COMPETITION"];
        const randomTemplate = teamCompTemplates[Math.floor(Math.random() * teamCompTemplates.length)];
        
        // Replace with appropriate team competition
        plan.drills[badDrillIndex] = {
          name: "Team Competition",
          duration: 10,
          description: `Fun, high-energy team competition: ${randomTemplate.name}`,
          focus_area: "Team",
          position_groups: ["ALL"],
          steps: [
            {
              name: "Team Competition Setup",
              setup: randomTemplate.setup,
              execution: randomTemplate.execution,
              coachingPoints: randomTemplate.coachingPoints
            }
          ]
        };
      }
    }
  }
}

/**
 * Helper function to ensure there are water breaks in the plan
 */
function normalizePositionGroupArray(positionGroups: any, forceAll: boolean): Array<{ group_name: string; steps: any[] }> {
  if (forceAll) {
    const mergedSteps: any[] = [];
    if (Array.isArray(positionGroups)) {
      for (const pg of positionGroups) {
        if (pg && typeof pg === 'object' && Array.isArray(pg.steps)) {
          mergedSteps.push(...pg.steps);
        }
      }
    }
    return [{ group_name: 'ALL', steps: mergedSteps }];
  }

  if (!Array.isArray(positionGroups) || positionGroups.length === 0) {
    return [{ group_name: 'ALL', steps: [] }];
  }

  return positionGroups.map((pg: any) =>
    typeof pg === 'string'
      ? { group_name: pg, steps: [] }
      : { group_name: pg.group_name || 'ALL', steps: Array.isArray(pg.steps) ? pg.steps : [] }
  );
}

function isWarmupSegment(drill: any): boolean {
  const name = (drill?.name || '').toLowerCase();
  return name.includes('warmup') || name.includes('warm-up') || name.includes('dynamic warm');
}

function isWaterBreakSegment(drill: any): boolean {
  const name = (drill?.name || '').toLowerCase();
  const desc = (drill?.description || '').toLowerCase();
  return (
    name.includes('water break') ||
    name.includes('hydration break') ||
    name.includes('hydration') ||
    (name.includes('break') && (name.includes('water') || name.includes('hydrate') || name.includes('rest'))) ||
    desc.includes('hydration') ||
    desc.includes('water break')
  );
}

function buildHydrationBreakName(index: number): string {
  return `Hydration Break ${index}`;
}

function isCooldownSegment(drill: any): boolean {
  const name = (drill?.name || '').toLowerCase();
  const desc = (drill?.description || '').toLowerCase();
  return name.includes('cool down') || name.includes('cooldown') || desc.includes('cool down') || desc.includes('cooldown');
}

function isDebriefSegment(drill: any): boolean {
  const name = (drill?.name || '').toLowerCase();
  const desc = (drill?.description || '').toLowerCase();
  return name.includes('debrief') || desc.includes('debrief');
}

function removeStepNumbering(stepName: string): string {
  const cleaned = stepName
    .replace(/^\s*(step\s*)?\d+[\)\].:-]?\s*/i, '')
    .replace(/^\s*option\s*\d*[\)\].:-]?\s*/i, '')
    .replace(/^\s*alternative\s*option\s*\d*[\)\].:-]?\s*/i, '')
    .replace(/^\s*[-*]\s*/, '')
    .trim();
  return cleaned || 'Drill Step';
}

function normalizeStepOptionsForDrill(drill: any, targetStepCount: number): any {
  const isWarmup = isWarmupSegment(drill);
  const isWaterBreak = isWaterBreakSegment(drill);
  const isCooldown = isCooldownSegment(drill);
  if (isWarmup || isWaterBreak || isCooldown) {
    return drill;
  }

  const normalized = { ...drill };
  if (!Array.isArray(normalized.position_groups)) {
    normalized.position_groups = [{ group_name: 'ALL', steps: [] }];
  }

  normalized.position_groups = normalized.position_groups.map((group: any) => {
    const steps = Array.isArray(group.steps) ? [...group.steps] : [];
    const cleaned = steps.map((step: any) => ({
      ...step,
      name: removeStepNumbering(step?.name || 'Drill Option')
    }));

    const capped = cleaned.slice(0, targetStepCount);
    while (capped.length < targetStepCount) {
      capped.push({
        name: `Alternative Option ${capped.length + 1}`,
        setup: 'Use standard setup for this drill segment.',
        execution: 'Choose one or two options that best fit today and save remaining options for a future practice.',
        coachingPoints: ['Prioritize quality reps over volume'],
        equipment: ''
      });
    }

    return {
      ...group,
      steps: capped
    };
  });

  return normalized;
}

function sanitizeAllStepNames(drill: any): any {
  const normalized = { ...drill };
  if (!Array.isArray(normalized.position_groups)) return normalized;
  normalized.position_groups = normalized.position_groups.map((group: any) => {
    const steps = Array.isArray(group.steps) ? group.steps : [];
    return {
      ...group,
      steps: steps.map((step: any) => ({
        ...step,
        name: removeStepNumbering(step?.name || '')
      }))
    };
  });
  return normalized;
}

function ensureWaterBreaks(plan: any): any {
  const waterBreaks = plan.drills.filter((drill: any) => isWaterBreakSegment(drill));
  
  // Check if we need to add water breaks
  const totalDuration = plan.drills.reduce((sum: number, drill: any) => sum + drill.duration, 0);
  const targetBreaks = Math.max(1, Math.floor(totalDuration / 35)); // At least one logical break for normal practice lengths
  
  // ALWAYS ensure we have at least 2 water breaks for practices longer than 60 minutes
  const minimumBreaks = totalDuration >= 45 ? 1 : 0;
  const finalTargetBreaks = Math.max(minimumBreaks, targetBreaks);
  
  if (waterBreaks.length < finalTargetBreaks) {
    // Find appropriate positions to insert water breaks
    // We want to spread them out evenly through the practice
    const drillCount = plan.drills.length;
    const interval = Math.floor(drillCount / (finalTargetBreaks + 1));
    
    // Create new array to hold the drills with water breaks
    const newDrills = [...plan.drills];
    let addedBreaks = 0;
    
    // Add the water breaks
    for (let i = 1; i <= finalTargetBreaks && addedBreaks < (finalTargetBreaks - waterBreaks.length); i++) {
      const insertPosition = i * interval + addedBreaks; // Adjust for previously added breaks
      
      if (insertPosition >= newDrills.length) {
        // Insert at the end if we're past the array length
        const waterBreak = {
          name: buildHydrationBreakName(waterBreaks.length + addedBreaks + 1),
          duration: 5,
          description: "Hydration and rest period",
          focus_area: "Team",
          intensity_level: undefined, // Water breaks don't have an intensity level
          position_groups: ["ALL"],
          steps: [
            {
              name: "Hydration",
              setup: "Water bottles available for all players",
              execution: "Players hydrate and take a brief rest",
              coachingPoints: ["Ensure all players hydrate properly", "Review upcoming drills if needed"]
            }
          ]
        };
        newDrills.push(waterBreak);
        addedBreaks++;
        continue;
      }
      
      // Check if there's already a water break within 2 drills
      const hasNearbyWaterBreak = newDrills.slice(
        Math.max(0, insertPosition - 2), 
        Math.min(newDrills.length, insertPosition + 2)
      ).some((drill: any) => isWaterBreakSegment(drill));
      
      if (!hasNearbyWaterBreak) {
        const waterBreak = {
          name: buildHydrationBreakName(waterBreaks.length + addedBreaks + 1),
          duration: 5,
          description: "Hydration and rest period",
          focus_area: "Team",
          intensity_level: undefined, // Water breaks don't have an intensity level
          position_groups: ["ALL"],
          steps: [
            {
              name: "Hydration",
              setup: "Water bottles available for all players",
              execution: "Players hydrate and take a brief rest",
              coachingPoints: ["Ensure all players hydrate properly", "Review upcoming drills if needed"]
            }
          ]
        };
        
        newDrills.splice(insertPosition, 0, waterBreak);
        addedBreaks++;
      }
    }
    
    plan.drills = newDrills;
  }
  
  return plan;
}

/**
 * Normalize drill durations so total equals targetDurationMinutes (practice end time).
 * Adjusts the last non-water-break drill to absorb the difference.
 */
function normalizeDrillDurations(plan: GeneratedPracticePlan, targetDurationMinutes: number): void {
  const drills = plan.drills;
  if (!drills.length) return;
  const total = drills.reduce((sum: number, d: any) => sum + (d.duration || 0), 0);
  const diff = targetDurationMinutes - total;
  if (diff === 0) return;

  const isWaterBreak = (d: any) => {
    const n = (d.name || '').toLowerCase();
    return n.includes('water') || n.includes('hydration') || (n.includes('break') && (n.includes('rest') || n.includes('hydrate')));
  };
  let lastIdx = -1;
  for (let i = drills.length - 1; i >= 0; i--) {
    if (!isWaterBreak(drills[i])) {
      lastIdx = i;
      break;
    }
  }
  if (lastIdx === -1) return;
  const d = drills[lastIdx];
  const current = d.duration || 0;
  const newDuration = Math.max(1, current + diff);
  d.duration = newDuration;
}

/**
 * Helper function to validate the plan structure and correct common issues
 */
const validateAndFixPlan = (planJson: any): GeneratedPracticePlan => {
  try {
    // Ensure we have the practice object
    if (!planJson.practice) {
      planJson.practice = {
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        end_time: "11:00",
        location: "Main Field",
        theme: "Team Day",
        drills: [] // This will be removed
      };
    }
    
    // Ensure we have the drills array
    if (!planJson.drills) {
      planJson.drills = [];
    }

    // Remove debrief-only segments; cooldowns should be kept as team-wide moments.
    planJson.drills = planJson.drills.filter((d: any) => !isDebriefSegment(d));
    
    // Handle case where drills got nested inside practice
    if (Array.isArray(planJson.practice.drills) && planJson.practice.drills.length > 0) {
      // Move drills from practice to the top level if needed
      if (planJson.drills.length === 0) {
        planJson.drills = planJson.practice.drills;
      }
      
      // Remove drills from practice
      delete planJson.practice.drills;
    }
    
    // Validate practice fields
    const practice = planJson.practice;
    if (!practice.team_id) practice.team_id = '';
    if (!practice.date) practice.date = new Date().toISOString().split('T')[0];
    if (!practice.start_time) practice.start_time = "09:00";
    if (!practice.end_time) practice.end_time = "11:00";
    if (!practice.location) practice.location = "Main Field";
    if (!practice.theme) practice.theme = "Team Day";
    
    // Process each drill to ensure it has all required fields
    planJson.drills = planJson.drills.map((drill: any) => {
      // Ensure drill properties
      if (!drill.name) drill.name = "Unnamed Drill";
      if (!drill.duration) drill.duration = 10;
      if (!drill.description) drill.description = "";
      if (!drill.focus_area) drill.focus_area = "Team";
      if (!drill.position_groups) drill.position_groups = ["ALL"];
      if (!drill.steps || !Array.isArray(drill.steps)) drill.steps = [];
      
      const isWarmup = isWarmupSegment(drill);
      const isWaterBreak = isWaterBreakSegment(drill);
      const isCooldown = isCooldownSegment(drill);
      const forceAll = isWarmup || isWaterBreak || isCooldown;
      drill.position_groups = normalizePositionGroupArray(drill.position_groups, forceAll);

      // Set default intensity_level if not provided
      // Check if it's a water break (null) or set default to "Technique focused"
      const drillNameLower = (drill.name || '').toLowerCase();
      const descLower = (drill.description || '').toLowerCase();
      const isCompetitive = drillNameLower.includes('competitive') || descLower.includes('competitive');

      if (isCompetitive) {
        drill.intensity_level = 'Game speed';
      } else if (drill.intensity_level === undefined || drill.intensity_level === null) {
        drill.intensity_level = isWaterBreak ? null : 'Technique focused';
      }
      
      // Process steps
      drill.steps = drill.steps.map((step: any) => {
        if (!step.name) step.name = "Unnamed Step";
        if (!step.setup) step.setup = "No setup provided";
        if (!step.execution) step.execution = "No execution instructions provided";
        if (!step.coachingPoints || !Array.isArray(step.coachingPoints)) {
          step.coachingPoints = ["Focus on proper form"];
        }
        return step;
      });
      
      return drill;
    });
    
    // Ensure there's at least one water break per hour
    planJson = ensureWaterBreaks(planJson);
    
    // Ensure there's a Team Competition period at the end
    ensureTeamCompetitionPeriod(planJson);
    
    // Return the validated and fixed plan
    return {
      practice: {
        ...planJson.practice,
        team_id: planJson.practice.team_id || '',
      },
      drills: planJson.drills || []
    };
  } catch (error) {
    console.error("Error in validateAndFixPlan:", error);
    
    // Return a minimal valid structure
    return {
      practice: {
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        end_time: "11:00",
        location: "Main Field",
        theme: "Team Day",
      },
      drills: []
    };
  }
};

// Step 1 & 2: Generate practice plan outline
const generatePracticePlanOutline = async (
  team: Team,
  duration: number,
  focusAreas: string[],
  theme: PracticeTheme,
  usePositionGroups: boolean
): Promise<GeneratedPracticePlan> => {
  try {
    // Combine base prompt and sport-specific prompt
    const basePrompt = BASE_PRACTICE_OUTLINE_PROMPT(team, duration, focusAreas, theme as any, undefined, undefined, undefined, { usePositionGroups });
    const sportSpecificPrompt = getSportSpecificPrompt(team, duration, focusAreas, theme as any, { usePositionGroups });
    const combinedPrompt = `${basePrompt}\n\n${sportSpecificPrompt}`;

    console.log('[AI Service] Generating practice plan with model:', GPT_MODEL);

    console.log('Outline prompt:', combinedPrompt);
    
    const messages: OpenAIMessage[] = [
      {
        role: 'system',
        content: 'You are an experienced youth sports coach creating detailed practice plans. Follow the exact structure provided.'
      },
      {
        role: 'user',
        content: combinedPrompt
      }
    ];

    const response = await callOpenAI({
      messages,
      model: GPT_MODEL,
      response_format: { type: "json_object" },
      ...(currentModelConfig.useMaxTokens && { max_tokens: currentModelConfig.maxTokens }),
      ...(currentModelConfig.useMaxCompletionTokens && { max_completion_tokens: currentModelConfig.maxTokens }),
      ...(currentModelConfig.supportsTemperature && { temperature: currentModelConfig.temperature })
    }, currentModelConfig.timeout);

    // The response is now the parsed data directly from callOpenAI
    if (!response.choices || !response.choices[0] || !response.choices[0].message) {
      console.error('Unexpected API response structure:', response);
      throw new AiServiceError('Unexpected API response structure', AiServiceErrorType.PARSING_ERROR);
    }
    
    const responseContent = response.choices[0].message.content;
    
    if (!responseContent || responseContent.length === 0) {
      console.error('[AI Service] Empty response content from API');
      throw new AiServiceError('API returned empty response content', AiServiceErrorType.PARSING_ERROR);
    }

    // Validate the response structure
    let parsedResponse;
    try {
      console.log('Raw AI response length:', responseContent.length);
      console.log('Raw AI response (first 500 chars):', responseContent.substring(0, 500));
      console.log('Raw AI response (last 500 chars):', responseContent.substring(Math.max(0, responseContent.length - 500)));
      
      parsedResponse = JSON.parse(responseContent);
    } catch (parseError) {
      console.error('[AI Service] JSON Parse Error:', parseError);
      
      // Try to find and fix common JSON issues
      let fixedContent = responseContent;
      
      // Remove any trailing commas before closing braces/brackets
      fixedContent = fixedContent.replace(/,(\s*[}\]])/g, '$1');
      
      // Ensure the JSON ends properly
      if (!fixedContent.trim().endsWith('}')) {
        fixedContent = fixedContent.trim() + '}';
      }
      
      try {
        parsedResponse = JSON.parse(fixedContent);
      } catch (fixError) {
      throw new AiServiceError(
        `JSON parsing failed: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`,
        AiServiceErrorType.PARSING_ERROR
      );
      }
    }
    
    if (!parsedResponse.drills || !Array.isArray(parsedResponse.drills)) {
      throw new AiServiceError('Invalid response structure: missing or invalid drills array', AiServiceErrorType.PARSING_ERROR);
    }

    return parsedResponse;
  } catch (error) {
    console.error('Error in generatePracticePlanOutline:', error);
    
    // Handle timeout errors specifically
    if (error instanceof Error) {
      if (error.name === 'TimeoutError') {
        throw new AiServiceError(
          `API request timed out after ${currentModelConfig.timeout / 1000} seconds. The response may be incomplete. Consider reducing the prompt length or using a faster model.`,
          AiServiceErrorType.API_ERROR
        );
      }
      if (error.message.includes('aborted')) {
        throw new AiServiceError(
          'API request was aborted due to timeout. The response may be incomplete.',
          AiServiceErrorType.API_ERROR
        );
      }
    }
    
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate practice plan outline',
      AiServiceErrorType.PARSING_ERROR
    );
  }
};

// Step 3: Generate drill details
const generateDrillDetails = async (
  drillName: string,
  positionGroups: string[],
  drillDuration: number,
  drillStepsMinimum: number,
  team: Team
): Promise<DetailedDrill> => {
  try {
    const prompt = DRILL_DETAILS_PROMPT(drillName, positionGroups, drillDuration, drillStepsMinimum, team);
    
    const messages: OpenAIMessage[] = [
      {
        role: 'system',
        content: 'You are a sports coach providing detailed drill instructions.'
      },
      {
        role: 'user',
        content: prompt
      }
    ];

    const response = await callOpenAI({
      messages,
      model: GPT_MODEL,
      response_format: { type: "json_object" }
    }, currentModelConfig.timeout);

    // Add JSON parsing error handling
    try {
      const responseContent = response.choices[0].message.content;
      const parsedResponse = JSON.parse(responseContent);
      return parsedResponse.drills[0];
    } catch (parseError) {
      console.error('[AI Service] Drill details JSON parsing failed:', parseError);
      throw new AiServiceError(
        `Drill details JSON parsing failed: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`,
        AiServiceErrorType.PARSING_ERROR
      );
    }
  } catch (error) {
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate drill details',
      AiServiceErrorType.PARSING_ERROR
    );
  }
};

// // Keep the existing findDrillVideo function for backward compatibility, 
// // but make it use the new utility function
// export const findDrillVideo = async (drillName: string, sport: Sport, team: Team, drill?: any): Promise<string> => {
//   try {
//     // Use "tackle football" explicitly if needed
//     const sportTerm = sport === 'Football' ? 'tackle football' : sport;
    
//     // Try to get position group if available from the drill
//     let positionGroupTerm = '';
//     if (drill && drill.position_groups && drill.position_groups.length > 0) {
//       const firstGroup = drill.position_groups[0];
//       positionGroupTerm = typeof firstGroup === 'string' ? firstGroup : 
//                           (firstGroup.group_name ? `${firstGroup.group_name} ` : '');
//     }
    
//     // Create a more structured search query
//     const searchQuery = `${sportTerm} ${positionGroupTerm}coaching drill "${drillName.trim()}"`;
//     console.log(`Finding video for drill with search query: "${searchQuery}"`);
    
//     // Instead of calling OpenAI, directly create a YouTube search URL
//     const youtubeSearchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
//     console.log('Generated YouTube search URL:', youtubeSearchUrl);
    
//     return youtubeSearchUrl;
//   } catch (error) {
//     console.error('Error finding drill video:', error);
//     // Return a search URL as a fallback
//     const sportTerm = sport === 'Football' ? 'tackle football' : sport;
//     return `https://www.youtube.com/results?search_query=${encodeURIComponent(`${sportTerm} drill ${drillName}`)}`;
//   }
// };

// Update the practice plan generator to automatically add video URLs
export const generatePracticePlan = async (
  request: PracticePlanRequest,
  onProgress?: (progress: { 
    status: 'generating' | 'completed' | 'count-update'; 
    drillName?: string; 
    drill?: DetailedDrill;
    totalDrills?: number;
  }) => void
): Promise<GeneratedPracticePlan> => {
  try {
    const detailedDrills: DetailedDrill[] = [];

    // Step 1: Generate practice plan outline
    const outline = await generatePracticePlanOutline(
      request.team,
      request.duration,
      request.focusAreas,
      request.theme,
      request.usePositionGroups ?? request.team.aiUsePositionGroups ?? true
    );

    // If we have drills, update the progress with the count
    if (outline.drills && outline.drills.length > 0 && onProgress) {
      onProgress({
        status: 'count-update',
        totalDrills: outline.drills.length
      });
    }

    // Step 2: Generate details for each drill
    for (const drill of outline.drills) {
      // Notify UI that we're starting a new drill
      if (onProgress) {
        onProgress({ 
          status: 'generating', 
          drillName: drill.name, 
          drill: undefined 
        });
      }

      let detailedDrill: any;
      
      // Skip generating details for water breaks
      if (isWaterBreakSegment(drill)) {
        detailedDrill = {
          ...drill,
          intensity_level: null, // Water breaks always have null intensity_level
          position_groups: normalizePositionGroupArray(drill.position_groups, true)
        };
        detailedDrill = sanitizeAllStepNames(detailedDrill);
      } else {
        const isWarmup = isWarmupSegment(drill);
        const isCooldown = isCooldownSegment(drill);
        const forceAllGroups = isWarmup || isCooldown || !(request.usePositionGroups ?? request.team.aiUsePositionGroups ?? true);
        const requestedStepCount = Math.min(5, Math.max(1, request.desiredDrillStepsPerDrill ?? 3));
        const reasonableUpperBound = Math.max(2, Math.floor((drill.duration || 10) / 3));
        const detailStepMinimum = isWarmup
          ? Math.max(
              requestedStepCount,
              Math.min(10, Math.max(4, Math.floor((drill.duration || 10) / 2)))
            )
          : Math.min(requestedStepCount, reasonableUpperBound);

        // Generate detailed drill
        detailedDrill = await generateDrillDetails(
          drill.name,
          normalizePositionGroupArray(drill.position_groups, forceAllGroups).map((pg: any) => pg.group_name),
          drill.duration,
          detailStepMinimum,
          request.team
        );
        detailedDrill.position_groups = normalizePositionGroupArray(detailedDrill.position_groups, forceAllGroups);
        detailedDrill = normalizeStepOptionsForDrill(detailedDrill, detailStepMinimum);
        detailedDrill = sanitizeAllStepNames(detailedDrill);
        
        // Preserve intensity_level from outline if detailed drill doesn't have it
        // Or use the detailed drill's intensity_level if it has one
        if (!detailedDrill.intensity_level && drill.intensity_level !== undefined) {
          detailedDrill.intensity_level = drill.intensity_level;
        }
        // Any drill labeled "competitive" gets Game speed
        const nameLower = (detailedDrill.name || '').toLowerCase();
        const descLower = (detailedDrill.description || '').toLowerCase();
        if (nameLower.includes('competitive') || descLower.includes('competitive')) {
          detailedDrill.intensity_level = 'Game speed';
        }
        // Set default if still missing
        if (detailedDrill.intensity_level === undefined || detailedDrill.intensity_level === null) {
          const drillNameLower = (drill.name || '').toLowerCase();
          const isWaterBreak = drillNameLower.includes('water') || 
                              drillNameLower.includes('hydration') || 
                              (drillNameLower.includes('break') && (drillNameLower.includes('rest') || drillNameLower.includes('hydrate')));
          detailedDrill.intensity_level = isWaterBreak ? null : 'Technique focused';
        }
        
        // Generate YouTube search URLs for all steps
        if (detailedDrill.position_groups) {
          for (const posGroup of detailedDrill.position_groups) {
            if (typeof posGroup !== 'string' && posGroup.steps && Array.isArray(posGroup.steps)) {
              for (const step of posGroup.steps) {
                // Generate YouTube search URL for this specific step
                step.video_url = createYoutubeSearchUrl(
                  step.name,
                  request.team.sport,
                  typeof posGroup === 'string' ? posGroup : posGroup.group_name
                );
              }
            }
          }
        }
      }



      // Add the drill to our list
      detailedDrills.push(detailedDrill);

      // Notify UI that this drill is complete
      if (onProgress) {
        onProgress({ status: 'completed', drillName: drill.name, drill: detailedDrill });
      }
    }

    const plan = validateAndFixPlan({
      practice: outline.practice,
      drills: detailedDrills
    });
    normalizeDrillDurations(plan, request.duration);
    return plan;
  } catch (error) {
    throw new AiServiceError(
      error instanceof Error ? error.message : 'Failed to generate practice plan',
      AiServiceErrorType.API_ERROR
    );
  }
};

export const useAIService = () => {
  const generateGamePlan = async (prompt: string): Promise<any> => {
    try {
      const messages: OpenAIMessage[] = [
        {
          role: "system",
          content: "You are a football coach assistant. Generate detailed game plans based on the provided information. Always respond with valid JSON in the exact format requested. Do not wrap the response in markdown code blocks."
        },
        {
          role: "user",
          content: prompt
        }
      ];

      const response = await callOpenAI({
        messages,
        model: GPT_MODEL,
        response_format: { type: "json_object" },
        ...(currentModelConfig.useMaxTokens && { max_tokens: currentModelConfig.maxTokens }),
        ...(currentModelConfig.useMaxCompletionTokens && { max_completion_tokens: currentModelConfig.maxTokens }),
        ...(currentModelConfig.supportsTemperature && { temperature: currentModelConfig.temperature })
      }, currentModelConfig.timeout);

      console.log('[AI Service] Raw OpenAI response:', response);

      const content = response.choices[0]?.message?.content;
      if (!content) {
        console.error('[AI Service] No content in AI response');
        throw new Error('No content in AI response');
      }

      console.log('[AI Service] AI response content:', content);

      try {
        // Strip markdown code blocks if present
        const cleanContent = content.replace(/```json\n?|\n?```/g, '').trim();
        
        // const parsedContent = JSON.parse(cleanContent);
        return cleanContent; // Return the cleaned content for the component to parse
      } catch (e) {
        console.error('[AI Service] Failed to parse AI response as JSON:', e);
        throw new Error('Failed to parse AI response as JSON');
      }
    } catch (error) {
      console.error('[AI Service] Error in generateGamePlan:', error);
      throw error;
    }
  };

  return {
    generateGamePlan
  };
}; 